"use client";

import Link from "./LocaleLink";
import Image from "next/image";
import { useEffect, useState } from "react";
import { usePathname } from "next/navigation";
import { useLocale } from "./LocaleProvider";
import LanguageSwitcher from "./LanguageSwitcher";

const copy = {
  en: {
    home: "Home",
    dental: "Dental",
    medical: "Medical",
    priceList: "Price List",
    joinUs: "Join Us",
    contact: "Contact",
    callUs: "Call Us",
    bookOnline: "Book Online",
    bookShort: "Book Online",
    toggleMenu: "Toggle menu",
    closeMenu: "Close menu",
    callStanmore: "Call Stanmore:",
    callWalton: "Call Walton-on-Thames:",
  },
  ro: {
    home: "Acasă",
    dental: "Stomatologie",
    medical: "Medical",
    priceList: "Prețuri",
    joinUs: "Carieră",
    contact: "Contact",
    callUs: "Sună-ne",
    bookOnline: "Programare online",
    bookShort: "Programări",
    toggleMenu: "Deschide meniul",
    closeMenu: "Închide meniul",
    callStanmore: "Sună Stanmore:",
    callWalton: "Sună Walton-on-Thames:",
  },
} as const;

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const pathname = usePathname();
  const t = copy[useLocale()];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
  }, [open]);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  return (
    <header
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        scrolled
          ? "bg-black/80 backdrop-blur border-b border-white/10 shadow-[0_2px_12px_rgba(0,0,0,0.45)]"
          : "bg-transparent border-transparent"
      }`}
    >
      <div className="container-px mx-auto flex items-center justify-between py-4">
        {/* LOGO */}
        <Link href="/" className="relative h-10 w-[170px] md:h-12 md:w-[190px]">
          <Image
            src="/logo.png"
            alt="EgoDent"
            fill
            sizes="240px"
            className="object-contain brightness-0 invert"
            priority
          />
        </Link>

        {/* DESKTOP NAV */}
        <nav className="hidden lg:flex items-center gap-4 xl:gap-8 text-[14px] xl:text-[15px] font-semibold whitespace-nowrap">
          <Link href="/" className="text-white/90 hover:text-white">
            {t.home}
          </Link>
          <Link href="/dental" className="text-white/90 hover:text-white">
            {t.dental}
          </Link>
          <Link href="/medical" className="text-white/90 hover:text-white">
            {t.medical}
          </Link>
          <Link href="/price-list" className="text-white/90 hover:text-white">
            {t.priceList}
          </Link>
          <Link href="/join-us" className="text-white/90 hover:text-white">
            {t.joinUs}
          </Link>
          <Link href="/contact" className="text-white/90 hover:text-white">
            {t.contact}
          </Link>

          {/* CALL US DROPDOWN */}
          <div className="relative group">
            <button className="text-white/90 hover:text-white inline-flex items-center gap-1.5">
              {t.callUs}
              <svg className="h-4 w-4 opacity-80" viewBox="0 0 20 20" fill="currentColor">
                <path d="M5.23 7.21a.75.75 0 011.06.02L10 11.2l3.71-3.97a.75.75 0 111.08 1.04l-4.25 4.55a.75.75 0 01-1.08 0L5.21 8.27a.75.75 0 01.02-1.06z" />
              </svg>
            </button>

            <div className="absolute right-0 mt-2 hidden group-hover:block">
              <div className="min-w-[220px] rounded-xl border border-white/10 bg-black/90 backdrop-blur p-3 shadow-xl">
                <a
                  href="tel:+442033016323"
                  className="block rounded-lg px-3 py-2 text-white/90 hover:text-[#27E8A7]"
                >
                  Stanmore:<br /> 02033 016323
                </a>
                <a
                  href="tel:+441932988460"
                  className="block rounded-lg px-3 py-2 text-white/90 hover:text-[#27E8A7]"
                >
                  Walton-on-Thames:<br /> 01932 988460
                </a>
              </div>
            </div>
          </div>

          {/* BOOK ONLINE – INTERNAL ONLY */}
          <Link
            href="/book-online"
            className="px-5 py-2 rounded-full font-semibold text-sm bg-[#27E8A7] text-black hover:scale-[1.05] transition"
          >
            {t.bookShort}
          </Link>

          <LanguageSwitcher />
        </nav>

        {/* MOBILE BURGER */}
        <button
          onClick={() => setOpen((v) => !v)}
          className="lg:hidden text-white relative z-[80]"
          aria-label={t.toggleMenu}
          aria-expanded={open}
        >
          {open ? (
            <svg className="h-7 w-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          ) : (
            <svg className="h-7 w-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16m-7 6h7" />
            </svg>
          )}
        </button>
      </div>

      {/* MOBILE MENU (FULLSCREEN OVERLAY) */}
      {open && (
        <div className="lg:hidden fixed inset-0 z-[60]">
          {/* Backdrop */}
          <button
            aria-label={t.closeMenu}
            className="absolute inset-0 bg-black/85"
            onClick={() => setOpen(false)}
          />

          {/* Panel */}
          <div className="absolute top-0 left-0 right-0 pt-[76px]">
            <div className="mx-4 rounded-2xl border border-white/10 bg-[#0B0F10] shadow-[0_20px_60px_rgba(0,0,0,0.65)]">
              <nav className="flex flex-col p-5 text-[16px]">
                {[
                  [t.home, "/"],
                  [t.dental, "/dental"],
                  [t.medical, "/medical"],
                  [t.priceList, "/price-list"],
                  [t.joinUs, "/join-us"],
                  [t.contact, "/contact"],
                ].map(([label, href]) => (
                  <Link
                    key={href}
                    href={href}
                    onClick={() => setOpen(false)}
                    className="w-full rounded-xl px-3 py-3 text-white/90 hover:text-white hover:bg-white/5 transition"
                  >
                    {label}
                  </Link>
                ))}

                <div className="my-4 h-px w-full bg-white/10" />

                <Link
                  href="/book-online"
                  onClick={() => setOpen(false)}
                  className="inline-flex w-full items-center justify-center rounded-full px-5 py-3 text-sm font-semibold bg-[#27E8A7] text-black hover:scale-[1.02] transition"
                >
                  {t.bookOnline}
                </Link>

                <div className="mt-4 flex justify-center">
                  <LanguageSwitcher />
                </div>

                <div className="mt-4 grid grid-cols-1 gap-2">
                  <a
                    href="tel:+442033016323"
                    className="rounded-xl px-3 py-3 text-white/80 hover:text-[#27E8A7] hover:bg-white/5 transition"
                  >
                    {t.callStanmore} 02033 016323
                  </a>
                  <a
                    href="tel:+441932988460"
                    className="rounded-xl px-3 py-3 text-white/80 hover:text-[#27E8A7] hover:bg-white/5 transition"
                  >
                    {t.callWalton} 01932 988460
                  </a>
                </div>
              </nav>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
